import { AfterViewInit, Component, ElementRef, inject, Injector, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs/operators';
import * as QRCode from 'qrcode';
import { InternalUserAuthViewModel } from '../internal-user-auth-view-model.service';
import { SessionService } from '../../../../../../services/session-service';
import { ClipboardService } from '../../../../../../services/clipboard-service';
import { InternalUser } from '../../../../../../models/users/dto/internal-user';
import { AuthCodeRequest } from '../../../../../../models/users/requests/auth-code-request';
import { UserChallengeName } from '../../../../../../models/users/enum/user-challenge.name';
import { NumberUtils } from '../../../../../../utils/number-utils';
import { ModalQrSupport } from '../../../../../../modals/modal-qr-code-support';
import { ButtonTypeEnum } from '../../../../../../ui/buttons/button/button-type.enum';
import { TextInputComponent } from '../../../../../../ui/form-components/text-input/text-input.component';

@Component({
  selector: 'app-internal-user-auth-setup',
  templateUrl: './internal-user-auth-setup.component.html',
  styleUrls: ['./internal-user-auth-setup.component.scss', '../../auth.component.scss']
})
export class InternalUserAuthSetupComponent implements AfterViewInit {
  private viewModel = inject(InternalUserAuthViewModel);
  private sessionService = inject(SessionService);
  private ngbModal = inject(NgbModal);
  private injector = inject(Injector);
  private clipboardService = inject(ClipboardService);

  @ViewChild('qrCode', { static: true }) qrCode!: ElementRef<HTMLCanvasElement>;
  @ViewChild('textInput', { static: true }) textInput!: TextInputComponent;

  public isMobile$ = this.viewModel.isMobile$;

  public mfaKey = '';

  public formSubmitted = false;

  public formGroup = new FormGroup({
    timeBasedOneTimePassword: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(6), Validators.maxLength(6)]
    })
  });

  public customErrorMessages = {
    timeBasedOneTimePassword: {
      required: $localize`Verification code is required`,
      minlength: $localize`Verification code must be 6 digits`,
      maxlength: $localize`Verification code must be 6 digits`
    }
  };

  private email = '';
  private challengeName: UserChallengeName | null = null;
  private sessionId = '';

  public ngAfterViewInit(): void {
    this.textInput.input.nativeElement.focus();

    this.sessionService.sessionContainer$.pipe(take(1)).subscribe(session => {
      const container = session ?? this.viewModel.getCachedSession();
      const user = container?.user as InternalUser;
      if (!user) return;
      this.email = user.email ?? '';
      this.challengeName = user.challengeName ?? null;
      this.sessionId = user.sessionId ?? '';
      this.mfaKey = user.mfaSecretCode ?? '';
      const url = `otpauth://totp/${user.email}%20(Internal)?secret=${user.mfaSecretCode}`;
      QRCode.toCanvas(this.qrCode.nativeElement, url).then();
    });
  }

  public copyToClipboard(code: string): void {
    this.clipboardService.copyToClipboard(code);
  }

  public verifyMfaCode(): void {
    this.formSubmitted = true;
    if (!this.formGroup.valid) return;
    const { timeBasedOneTimePassword } = this.formGroup.getRawValue();
    const req = new AuthCodeRequest(
      this.email,
      NumberUtils.formatNumberWithLeadingZeros(+timeBasedOneTimePassword, 6),
      this.challengeName,
      this.sessionId
    );
    this.viewModel.verifyMfaCode(req, true);
  }

  public openSupportModal(): void {
    ModalQrSupport.open(this.ngbModal, this.injector);
  }

  public openContactPage(): void {
    this.viewModel.openContactPage();
  }

  protected readonly ButtonTypeEnum = ButtonTypeEnum;
}
